import type { ValidationError } from './types';

/** Backend codes (see {@code PortConnectionRule}) for unwired ports. */
const INPUT_DISCONNECTED = 'INPUT_PORT_DISCONNECTED';
const OUTPUT_DISCONNECTED = 'OUTPUT_PORT_DISCONNECTED';

/**
 * Map a node's validation errors to the tooltip rendered on the run
 * button when it morphs into the "Check" affordance. The
 * orphan-combination strings are design copy:
 *
 *   - both input + output disconnected → "Not connected — missing input and output"
 *   - input only                       → "Missing input connection"
 *   - output only                      → "Missing output connection"
 *
 * For other errors we surface the first message verbatim; multi-error
 * mixes collapse to a count so the tooltip stays compact (the user
 * clicks through to the per-node inspector for the full list).
 *
 * Returns null when the input list is empty so callers can fall back
 * to their gate-state tooltip without reaching for ?? chains.
 */
export function nodeWarningTooltip(
  errors: readonly ValidationError[],
): string | null {
  if (errors.length === 0) return null;

  const hasOrphanIn = errors.some((e) => e.kind === INPUT_DISCONNECTED);
  const hasOrphanOut = errors.some((e) => e.kind === OUTPUT_DISCONNECTED);
  if (hasOrphanIn && hasOrphanOut) {
    return 'Not connected — missing input and output';
  }
  if (hasOrphanIn) return 'Missing input connection';
  if (hasOrphanOut) return 'Missing output connection';

  if (errors.length === 1) return errors[0].message;
  return `${errors.length} validation issues — open the inspector`;
}
